import type { TestItem } from "../../api/types";
import { type Deck, PITCH_RANGE, UNSTEADY_ABOVE } from "./engine";
import { JogWheel } from "./JogWheel";
import { Waveform } from "./Waveform";

/**
 * One slot: what is on it, its waveform, and the controls a deck has. The
 * deck itself is a mutable object; the page re-renders this when it says
 * something changed, and the slider and readouts read straight from it.
 */
export function DeckPanel({ slot, deck, item, status, onSync }: {
  slot: "A" | "B";
  deck: Deck;
  item: TestItem | null;
  /** What the page is doing to this slot, or what went wrong -- shown in
   *  place of the title line until the deck is loaded. */
  status: string | null;
  onSync: () => void;
}) {
  const bpm = deck.grid?.bpm ?? null;
  const heard = deck.effectiveBpm();
  const unsteady = deck.data !== null && deck.grid !== null && deck.data.fitErr > UNSTEADY_ABOVE;
  const canSync = deck.loaded && deck.grid !== null;

  return (
    <section className={`deck${deck.playing ? " playing" : ""}`}>
      <header className="deck-hd">
        <span className="slot">{slot}</span>
        {item ? (
          <span className="who">
            <b>{item.title}</b>
            <span className="dim"> {item.artist}</span>
            {item.key_ && <span className="key"> · {item.key_}</span>}
          </span>
        ) : (
          <span className="who dim">{status ?? "empty — load a track from the list below"}</span>
        )}
        {item && status && <span className="status dim">{status}</span>}
        <span className="tempo">
          {bpm !== null ? (
            <>
              <b className="heard">{heard!.toFixed(1)}</b>
              <span className="dim"> of {bpm.toFixed(1)} BPM</span>
            </>
          ) : deck.loaded ? (
            <span className="dim">no steady pulse</span>
          ) : null}
          {unsteady && <em className="badge" title={`the detector's beats sat ${deck.data!.fitErr.toFixed(3)} s from the grid on average`}>unsteady grid</em>}
        </span>
      </header>

      <Waveform deck={deck} />

      <div className="deck-ctl">
        <div className="transport">
          <button className="cue" disabled={!deck.loaded} title="back to the start"
                  onClick={() => { deck.seek(0); }}>⏮</button>
          <button className={`go${deck.playing ? " on" : ""}`} disabled={!deck.loaded}
                  title={deck.playing ? "pause" : "play"}
                  onClick={() => void deck.toggle().catch(() => undefined)}>
            {deck.playing ? "❚❚" : "▶"}
          </button>
          <button className="sync" disabled={!canSync} onClick={onSync}
                  title={`match this deck's tempo and beats to deck ${slot === "A" ? "B" : "A"}`}>Sync</button>
        </div>

        <JogWheel deck={deck} />

        <div className="fader">
          <button className="zero" onClick={() => deck.setPitch(0)} title="fader to zero">0%</button>
          <input type="range" min={-PITCH_RANGE} max={PITCH_RANGE} step={0.1} value={deck.pitch}
                 disabled={!deck.loaded}
                 onChange={(e) => deck.setPitch(Number(e.target.value))}
                 title={`±${PITCH_RANGE}%: the range of a 1210`} />
          <span className="read"><b>{deck.pitch > 0 ? "+" : ""}{deck.pitch.toFixed(1)}%</b></span>
        </div>

        <div className="switches">
          <label className={`chk${deck.keyLock ? " on" : ""}`} title="keep the pitch while the tempo moves">
            <input type="checkbox" checked={deck.keyLock} onChange={(e) => deck.setKeyLock(e.target.checked)} />
            key lock
          </label>
          <button className="bar" disabled={!canSync} onClick={() => deck.setBarShift(deck.barShift + 1)}
                  title="move the bar lines on by one beat, when the detector's first beat was not the one">
            bar · {deck.barShift + 1}
          </button>
        </div>
      </div>
    </section>
  );
}
